import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { ClientService } from '../client/client.service';

import { AuthenticationService } from '../authentication/authentication.service';
import { ConnectivityService } from '../connectivity/connectivity.service';

declare global {
  interface Navigator {
      msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

@Injectable({
  providedIn: 'root'
})
export class IoService {

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private clientService: ClientService,
    private connectivityService: ConnectivityService,
  ) { }

  post(path: string, postData: any = {}) {
    return new Promise((resolve, reject) => {
      if (this.authenticationService.isSignedIn()) {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token')
          })
        };

        if (!postData.rfId) {
          postData.rfId = this.clientService.getRfId();
        }

        if (path.substring(0, 1) === '/') {
          path = path.substr(1);
        }

        this.http.post<any>(this.clientService.getServerPath() + path, postData, httpOptions).toPromise()
          .then(response => {
            this.connectivityService.markOnline();
            resolve(response);
          })
          .catch(err => {
            // status 0 = the request never reached the server (Wi-Fi drop,
            // DNS, timeout) — flip the app into its offline state so the
            // scanning overlay appears instead of a silent failure.
            if (err && err.status === 0) {
              this.connectivityService.markOffline();
            }
            // A device-PIN session is re-validated against its device
            // authorization on every request; a 401 here means the device
            // was revoked (or expired) mid-session, so lock immediately
            // instead of letting every later call fail the same way.
            // Password sessions keep their existing behavior untouched.
            if (err && err.status === 401 && this.authenticationService.isDeviceSession()) {
              this.authenticationService.lock('deviceRevoked');
            }
            reject(err);
          });
      } else {
        reject('Expired authorization.');
      }
    });
  }

  upload(path: string, postData: any = {}, file) {
      if (!postData.rfId) {
        postData.rfId = this.clientService.getRfId();
      }

      // Build FormData object
      const formData = new FormData();
      formData.append('file', file);
      formData.append('data', JSON.stringify(postData));

      // Define post headers
      const headers = new HttpHeaders({
        'Authorization': 'Bearer ' + localStorage.getItem('token'),
        'rfId': postData.rfId
      });

      return this.http.post<any>(this.clientService.getServerPath() + path, formData, {
        headers,
        reportProgress: true,
        observe: 'events'
      });
  }

  download(path: string, postData: any = {}, filename: string = 'report.csv') {
    return new Promise<void>((resolve, reject) => {
      if (this.authenticationService.isSignedIn()) {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('token')
        });

        if (!postData.rfId) {
          postData.rfId = this.clientService.getRfId();
        }

        if (path.substring(0, 1) == '/') {
          path = path.substr(1);
        }

        this.http.post<any>(this.clientService.getServerPath() + path, postData, {
          headers,
          responseType: 'blob' as 'json'
        })
          .toPromise()
          .then(response => {

            if (navigator.msSaveBlob) {
                 // IE 10+
                navigator.msSaveBlob(new Blob([response], { type: 'text/csv;charset=utf-8;' }), filename);
            } else {
                const saveByteArray = (function() {
                    const a = document.createElement('a');
                    document.body.appendChild(a);
                    // a.style = "display: none";
                    return function(data, name) {
                        const blob = new Blob(data, {type: 'octet/stream'});
                        const url = window.URL.createObjectURL(blob);
                        a.href = url;
                        a.download = name;
                        a.click();
                        window.URL.revokeObjectURL(url);
                    };
                }());
                saveByteArray([response], filename);
            }

            resolve();
          })
          .catch(err => {
            reject(err);
          });
      } else {
        reject('Expired authorization.');
      }
    });
  }

  print = async (path: string, postData: any = {}) => {
    let serverResponse:any = await this.post(path, postData);
    // A fresh window per print — the old shared named window ("printWindow")
    // was never document.close()d, so every later print APPENDED below the
    // previous render. (No explicit document.open() before the write: it
    // races the new window's initial about:blank load and blanks the page.)
    let printWindow = window.open("", "_blank", "width=600,height=600");
    printWindow.document.write(serverResponse.rendered);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 1000);
  }

  async logError(message: string) {
    try {
      await this.post('client/logError', {
        message,
      });
    } catch (err) {
      console.error('Failed to log error to server. ', message);
    }
  }
}
